// App.js
import React, { useState } from "react";
import axios from "axios";

function App() {
  // Base URL for backend API (configure via .env: REACT_APP_API_URL=https://your-backend-url)
  const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const [url, setUrl] = useState("");
  const [meta, setMeta] = useState({});
  const [format] = useState("mp3");
  const [loading, setLoading] = useState(false);

  const getYoutubeVideoMeta = async (videoUrl) => {
    if (!videoUrl) return;
    const videoId = getYouTubeVideoId(videoUrl);
    const response = await axios.get(`${API_BASE}/api/video/meta`, {
      params: { video_id: videoId }
    });
    setMeta(response.data);
    return response;
  };

  const getYouTubeVideoId = (input) => {
    if (!input) return null;
    try {
      const parsed = new URL(input);
      const host = parsed.hostname.replace(/^www\./, "");

      // Standard watch URL: ?v=ID
      const v = parsed.searchParams.get("v");
      if (v) return v;

      // youtu.be short link
      if (host === "youtu.be") {
        const id = parsed.pathname.split("/").filter(Boolean)[0];
        if (id) return id;
      }

      // /embed/ID or /shorts/ID or /v/ID
      const path = parsed.pathname;
      const embedMatch = path.match(/\/(embed|shorts|v)\/([a-zA-Z0-9_-]{11})/);
      if (embedMatch) return embedMatch[2];

      // Some links might carry params in the hash
      if (parsed.hash) {
        const hashParams = new URLSearchParams(parsed.hash.replace(/^#/, ""));
        const hv = hashParams.get("v");
        if (hv) return hv;
      }
    } catch (e) {
      // Not a full URL; maybe it's already an ID
      if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;
    }

    // Fallback: pick the first 11-char ID-like token
    const looseMatch = input.match(/([a-zA-Z0-9_-]{11})/);
    if (looseMatch) return looseMatch[1];

    return null;
  };

  const handleDownload = async () => {
    const videoId = getYouTubeVideoId(url);
    if (!videoId) {
      alert("Lien YouTube invalide. Veuillez coller une URL valide.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/video/download`, {
        params: { video_id: videoId, format },
        responseType: "arraybuffer", // <--- obligatoire
      });
      console.log(response);

      // Try to read headers for correct type/name. Note: server must expose headers via CORS.
      const disposition = response.headers?.["content-disposition"] || "";

      // Build a Blob with the right MIME type
      const mimeType = format === "mp3" ? "audio/mpeg" : "video/mp4";
      const blob = new Blob([response.data], { type: mimeType });

      // Determine filename from Content-Disposition or fall back to meta.title or generic
      let fileName = `music.${format}`;
      const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
      if (match) {
        fileName = decodeURIComponent(match[1] || match[2]).trim();
      } else if (meta?.title) {
        // sanitize title for file systems
        const safeTitle = meta.title.replace(/[\\/:*?"<>|]/g, " ").trim();
        fileName = `${safeTitle || "music"}.${format}`;
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error(err);
      alert("Erreur lors du téléchargement. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-white">
      {/* Background accents */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-16 h-72 w-72 rounded-full bg-gradient-to-br from-zinc-200 to-white blur-3xl" />
        <div className="absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-gradient-to-tr from-white to-zinc-100 blur-3xl" />
      </div>

      <main className="relative z-10 mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-6">
        {/* Header */}
        <header className="mb-10 text-center">
          <h1 className="text-4xl font-semibold tracking-tight text-zinc-900">YouTube to MP3</h1>
          <p className="mt-3 text-zinc-500">Collez un lien YouTube et téléchargez en MP3.</p>
        </header>

        {/* Glass Card */}
        <section className="w-full rounded-2xl border border-white/30 bg-white/60 p-6 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.15)] backdrop-blur-xl sm:p-8">
          {/* Input + CTA */}
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <input
              type="text"
              placeholder="Colle le lien YouTube ici..."
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                getYoutubeVideoMeta(e.target.value);
              }}
              className="w-full rounded-xl border border-zinc-200 bg-white/70 px-4 py-3 text-zinc-900 placeholder:text-zinc-400 shadow-sm backdrop-blur focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
            />
            <button
              onClick={handleDownload}
              disabled={loading || !url}
              className={`inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-zinc-900/20 focus:ring-offset-1 focus:ring-offset-white ${
                loading || !url
                  ? "bg-zinc-300 text-white cursor-not-allowed"
                  : "bg-zinc-900 text-white hover:shadow-md active:scale-[0.99]"
              }`}
            >
              {loading ? "Téléchargement..." : "Télécharger"}
            </button>
          </div>

          {/* Meta Preview */}
          {(meta?.title || meta?.thumbnail) && (
            <div className="mt-6 grid gap-4 sm:grid-cols-[96px_1fr]">
              {meta.thumbnail && (
                <img
                  src={meta.thumbnail}
                  alt={meta.title}
                  className="h-24 w-24 rounded-xl object-cover shadow"
                />
              )}
              <div>
                {meta.title && (
                  <p className="text-sm font-medium text-zinc-800 line-clamp-2">{meta.title}</p>
                )}
                {meta.channel && (
                  <p className="mt-1 text-xs text-zinc-500">{meta.channel}</p>
                )}
              </div>
            </div>
          )}
        </section>

        <footer className="mt-10 text-center text-xs text-zinc-400">Simple, rapide, minimaliste.</footer>
      </main>
    </div>
  );
}

export default App;

